import { useState } from 'react';
import { PreviousNextButtons } from '@/components/forms/other/PreviousNextButtons/PreviousNextButtons';
import { Grid, Typography, Alert, Divider } from '@mui/material';
import { WALLET_PHRASES, WALLET_CONFIRMATION_PHRASES } from '@/consts/phrases/walletPhrases';
import {
    IAccount,
    IWalletFileData,
    IWalletPaymentAllDetails,
    IWalletPaymentConfirmationRequestData,
    IWalletUploadedDocument,
} from '@/types/types';
import { WalletPaymentSteps } from '@/types/enums/forms/wallet/walletPaymentSteps';
import { isArrayWithValues } from '@/utils/fp/array/array';
import { walletService } from '@/services/api/walletService/walletService';
import { useRouter } from '@/hooks/general/useRouter/useRouter';
import { usePhrase } from '@/hooks/general/usePhrase/usePhrase';
import { useAccountDetailsQuery } from '@/hooks/queries/account/generalAccountQueries';
import { useServerErrors } from '@/hooks/forms/useServerErrors';
import { useForm } from 'react-hook-form';
import { TextField } from '@/components/forms/inputs/TextField/TextField';
import { CheckboxField } from '@/components/forms/inputs/CheckBoxField/CheckboxField';
import { ErrorAlert } from '@/components/forms/alerts/ErrorAlert/ErrorAlert';
import { WithSendCode } from '@/components/forms/wrappers/WithSendCode/WithSendCode';
import { PaymentAmount } from '@/components/blocks/wallet/PaymentAmount/PaymentAmount';
import { useWindowDimensions } from '@/hooks/general/useWindowDimensions/useWindowDimensions';
import { ScreenType } from '@/types/enums/media';
import { sendMobileCode, setMobileCodeError } from '@/utils/forms/sendMobileCode/sendMobileCode';
import { WalletUploadedFilesList } from '@/components/blocks/wallet/Payment/WalletUploadedFilesList/WalletUploadedFilesList';
import { WALLET_DETAILS_FILE_UPLOAD_FIELDS } from '@/components/blocks/wallet/Payment/WalletPaymentDetailsFields/WalletPaymentDetailsFields';
import { isValid, format } from 'date-fns';
import { config } from '@/config';
import { FormGridErrors } from '@/components/forms/alerts/FormGridErrors/FormGridErrors';
import { InfoTooltip } from '@/components/general/InfoTooltip/InfoTooltip';
import { GENERAL_PHRASES } from '@/consts/phrases/generalPhrases';
import { getCooldownFromError } from '@/utils/getFetchErrorMessage';

interface WalletConfirmationDetailsFormProps {
    data: IWalletPaymentAllDetails;
    selectedAccount: IAccount;
    updateWalletPaymentData: (data: Partial<IWalletPaymentAllDetails>) => void;
    documents: Record<string, IWalletUploadedDocument[]>;
}

export const WalletConfirmationDetailsForm = ({
    data,
    selectedAccount,
    updateWalletPaymentData,
    documents,
}: WalletConfirmationDetailsFormProps) => {
    const { t } = usePhrase();
    const { screenType } = useWindowDimensions();
    const router = useRouter();
    const [filePropertyErrors, setFilePropertyErrors] = useState<string>();
    const [mobilePhoneCodeId, setMobilePhoneCodeId] = useState<number>();

    const {
        isMobilePhoneConfirmed,
        isMobilePhoneOptional,
        paymentNotes,
        paymentSummary,
        vatAmount,
        ...initialData
    } = data.data[WalletPaymentSteps.ConfirmPaymentDetails];

    const {
        amount: amountVal,
        existingPaymentAmount,
        paymentMethodPhraseId,
    } = data.cachedData[WalletPaymentSteps.PaymentStart];

    const accountDetails = useAccountDetailsQuery();

    const returnToDetailsStep = () => updateWalletPaymentData({ currentStep: data.prevStep });

    const { handleSubmit, control, formState, getValues, setError, clearErrors } = useForm<
        Partial<IWalletPaymentConfirmationRequestData>
    >({
        mode: 'onChange',
        values: {
            compensationProvisions: undefined,
            mobilePhone: initialData?.mobilePhone ?? '',
            mobilePhoneCode: undefined,
        },
    });

    const { errors, isSubmitting } = formState;

    const { handleServerErrors, serverErrors } = useServerErrors({
        setError,
        clearErrors,
        getValues,
    });

    const convertAnyDateString = (str: string) => {
        const dateRegex =
            /^(\d{4}-\d{2}-\d{2}|\d{2}\/\d{2}\/\d{4}|\d{4}\/\d{2}\/\d{2}|\w+ \d{1,2}, \d{4}|\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z?)$/;
        if (!dateRegex.test(str)) {
            return str;
        }

        const parsedDate = new Date(str);
        if (isValid(parsedDate) && parsedDate.toString() !== 'Invalid Date') {
            return format(parsedDate, config.dateFormat);
        } else {
            return str;
        }
    };

    const handleSendMobileCode = async (): Promise<boolean | { secondsLeft: number }> => {
        try {
            const res = await sendMobileCode(getValues('mobilePhone'), accountDetails?.countryCode);
            const { success, error } = res;
            if (success) {
                setMobilePhoneCodeId(success.mobilePhoneCodeId);
                clearErrors('mobilePhone');
            }
            if (error) {
                setMobileCodeError(error, setError, t);
                const cooldown = getCooldownFromError(error.error);
                if (cooldown) return cooldown;
            }
            return !!success;
        } catch (err) {
            handleServerErrors(err);
            const cooldown = getCooldownFromError(err);
            if (cooldown) return cooldown;
            return false;
        }
    };

    const hasDocuments = (() => {
        if (!documents) return false;
        return Object.values(documents).some((arr) => arr && arr.length > 0);
    })();

    const readFileAsDataURL = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    };

    const handleFileUploadFieldsErrors = (err: unknown) => {
        if (typeof err === 'object' && err !== null && 'errors' in err) {
            const errorObj = err as {
                errors: Array<{ property: string; phraseId: number }>;
            };

            const filePropertyErrors = errorObj.errors
                .filter((error) => WALLET_DETAILS_FILE_UPLOAD_FIELDS.includes(error.property))
                .map((error) => t(error.phraseId));

            if (filePropertyErrors.length) {
                setFilePropertyErrors(filePropertyErrors.join('<br>'));
            } else {
                setFilePropertyErrors(undefined);
            }
        }
    };

    const handleSubmitData = async (vals: Partial<IWalletPaymentConfirmationRequestData>) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { lastPaymentMethodId, paymentMethodId, paymentMethodPhraseId, ...paymentStartData } =
            data.cachedData[WalletPaymentSteps.PaymentStart];

        const combinedData: Partial<IWalletPaymentConfirmationRequestData> = {
            ...paymentStartData,
            ...data.cachedData[WalletPaymentSteps.ProvidePaymentDetails],
            ...vals,
            smsCodeId: mobilePhoneCodeId,
            paymentMethodId: lastPaymentMethodId || paymentMethodId,
        };

        if (hasDocuments) {
            for (const [id, documentArray] of Object.entries(documents)) {
                combinedData[id] = [];
                for (const document of documentArray) {
                    if (document && document.file) {
                        const fileContent = await readFileAsDataURL(document.file);
                        (combinedData[id] as IWalletFileData[]).push({
                            name: document.file.name,
                            type: document.file.type,
                            content: fileContent,
                        });
                    }
                }
            }
        }

        try {
            if (!selectedAccount?.accountId) {
                throw new Error('Account id is not provided');
            }
            await walletService.submitConfirmationDetails(selectedAccount.accountId, combinedData);
            router.push('/wallet');
        } catch (err) {
            handleFileUploadFieldsErrors(err);
            handleServerErrors(err);
        }
    };

    return (
        <form onSubmit={handleSubmit(handleSubmitData)}>
            <Grid container spacing={2}>
                <FormGridErrors
                    errors={serverErrors}
                    gridSx={{ pb: 2 }}
                    generalErrorId="server_error_message_box"
                />
                {isArrayWithValues(paymentNotes) &&
                    paymentNotes.map((noteItem) => (
                        <Grid size={12} key={noteItem.phraseId}>
                            <Alert severity="info" icon={false}>
                                {t(noteItem.phraseId, {
                                    replaceTextVars: noteItem.phrasePlaceholders,
                                })}
                            </Alert>
                        </Grid>
                    ))}
                <Grid size={{ xs: 12, md: 6 }}>
                    <PaymentAmount
                        transactionData={selectedAccount}
                        amount={amountVal}
                        existingPaymentAmount={existingPaymentAmount}
                        vatAmount={vatAmount}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="greyLabel">
                        {t(WALLET_CONFIRMATION_PHRASES.PAYMENT_METHOD_LABEL)}:
                    </Typography>
                    <Typography variant="subtitle1">
                        {paymentMethodPhraseId ? t(paymentMethodPhraseId) : '-'}
                    </Typography>
                </Grid>
                {isArrayWithValues(paymentSummary) &&
                    paymentSummary.map((infoItem) => (
                        <Grid size={{ xs: 12, md: 6 }} key={infoItem?.phraseId || infoItem?.value}>
                            {infoItem?.phraseId && (
                                <Typography variant="greyLabel">
                                    {t(infoItem.phraseId)}:{' '}
                                </Typography>
                            )}
                            {infoItem?.value && (
                                <Typography variant="subtitle1" sx={{ wordBreak: 'break-word' }}>
                                    {convertAnyDateString(infoItem.value)}
                                </Typography>
                            )}
                        </Grid>
                    ))}
                {hasDocuments && <WalletUploadedFilesList documents={documents} />}
                <Grid size={12}>
                    <Divider sx={{ mb: 1 }} />
                </Grid>
                {!isMobilePhoneOptional && (
                    <Grid container size={12}>
                        <Grid size={{ xs: 12, md: 6 }} sx={{ pr: [0, 0, 2] }}>
                            <WithSendCode
                                codeType="phone"
                                hasSendCode={!isMobilePhoneOptional}
                                onSendCode={handleSendMobileCode}
                                buttonBreakpoint={screenType === ScreenType.Mobile ? 7 : 5}
                                codeFormInput={
                                    <TextField
                                        name="mobilePhoneCode"
                                        label={WALLET_CONFIRMATION_PHRASES.MOBILE_PHONE_CODE_LABEL}
                                        control={control}
                                        error={errors.mobilePhoneCode}
                                        autoFocus={false}
                                        fullWidth
                                    />
                                }
                            >
                                <TextField
                                    name="mobilePhone"
                                    label={WALLET_CONFIRMATION_PHRASES.MOBILE_PHONE_LABEL}
                                    control={control}
                                    disabled={isMobilePhoneConfirmed}
                                    error={errors.mobilePhone}
                                    fullWidth
                                    slotProps={{
                                        input: {
                                            endAdornment: (
                                                <InfoTooltip
                                                    title={GENERAL_PHRASES.PHONE_INFO_TOOLTIP}
                                                />
                                            ),
                                        },
                                    }}
                                />
                            </WithSendCode>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Alert
                                severity="info"
                                variant="outlined"
                                sx={{ borderWidth: 0, p: 0, mt: [2, 2, 0] }}
                            >
                                {t(WALLET_CONFIRMATION_PHRASES.PROTECT_DESCRIPTION)}
                            </Alert>
                        </Grid>
                    </Grid>
                )}
                <Grid size={12}>
                    <CheckboxField
                        control={control}
                        name="compensationProvisions"
                        label={WALLET_CONFIRMATION_PHRASES.COMPENSATION_AGREEMENT_LABEL}
                        error={errors.compensationProvisions}
                        required
                    />
                </Grid>
                {filePropertyErrors && (
                    <Grid size={12} sx={{ pt: 2 }} tabIndex={0}>
                        <ErrorAlert
                            id="file_property_errors_message_box"
                            errorMessage={filePropertyErrors}
                        />
                    </Grid>
                )}
            </Grid>
            <Grid
                container
                sx={{
                    justifyContent: 'flex-end',
                    marginTop: 2,
                }}
            >
                <PreviousNextButtons
                    onPreviousClicked={returnToDetailsStep}
                    nextDisabled={isSubmitting}
                    previousDisabled={isSubmitting}
                    prevLabel={WALLET_PHRASES.BACK_BUTTON}
                    nextLabel={WALLET_CONFIRMATION_PHRASES.CONFIRM_BUTTON}
                />
            </Grid>
        </form>
    );
};
